import dayjs from "dayjs";
import { sortByRecent } from "../../../helpers/sortByRecent.helper";

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

export const normalizeGrantTypes = (raw = {}) => {
  const value =
    raw.grantType ||
    raw.grantTypes ||
    raw.grant ||
    raw.grants ||
    [];
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (typeof value === "string" && value.trim()) return [value.trim()];
  return [];
};

export const unwrapClientPayload = (payload) => {
  if (!payload || typeof payload !== "object") return payload || {};
  if (payload.clientId || payload.name || payload.clientName) return payload;
  if (payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)) {
    return unwrapClientPayload(payload.data);
  }
  if (payload.result && typeof payload.result === "object" && !Array.isArray(payload.result)) {
    return unwrapClientPayload(payload.result);
  }
  return payload;
};

const toBool = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  }
  return false;
};

/** Returns boolean when the payload includes the flag; otherwise undefined. */
export const readOptionalBool = (raw, keys = []) => {
  if (!raw || typeof raw !== "object") return undefined;
  for (const key of keys) {
    if (
      Object.prototype.hasOwnProperty.call(raw, key) &&
      raw[key] !== undefined &&
      raw[key] !== null &&
      raw[key] !== ""
    ) {
      return toBool(raw[key]);
    }
  }
  return undefined;
};

export const normalizeClient = (rawInput = {}) => {
  const raw = unwrapClientPayload(rawInput);
  const clientId = firstDefined(raw.clientId, raw.id, raw._id, "");
  const roles = Array.isArray(raw.roles) ? raw.roles.filter(Boolean).map(String) : [];
  const grantType = normalizeGrantTypes(raw);
  // Prefer non-empty redirect lists; empty [] must not block other field names.
  const redirectCandidates = [
    raw.redirectUri,
    raw.redirectUris,
    raw.redirect_uri,
    raw.redirect_uris,
    raw.redirectURIs,
    raw.redirectURL,
    raw.redirectUrls,
  ];
  let redirectUri = [];
  for (const value of redirectCandidates) {
    if (Array.isArray(value) && value.length) {
      redirectUri = value.filter(Boolean).map(String);
      break;
    }
    if (typeof value === "string" && value.trim()) {
      redirectUri = [value.trim()];
      break;
    }
  }

  return {
    ...raw,
    id: clientId,
    clientId,
    name: firstDefined(raw.name, raw.clientName, ""),
    grantType,
    roles,
    redirectUri,
    privacyPolicy: String(
      firstDefined(
        raw.privacyPolicy,
        raw.privacy_policy,
        raw.privacyPolicyUrl,
        raw.privacyPolicyURI,
        ""
      ) || ""
    ),
    termsAndConditions: String(
      firstDefined(
        raw.termsAndConditions,
        raw.terms_and_conditions,
        raw.termsAndCondition,
        raw.termsAndConditionsUrl,
        raw.termsAndConditionsURI,
        raw.termsOfService,
        raw.terms_of_service,
        raw.termsOfUse,
        raw.termsUrl,
        raw.termsURI,
        raw.terms,
        ""
      ) || ""
    ),
    allowUserCreation: toBool(raw.allowUserCreation),
    allowSelfUserCreation: toBool(
      firstDefined(raw.allowSelfUserCreation, raw.allowSelfUserRegistration)
    ),
    allowLoginViaOTP: toBool(
      firstDefined(raw.allowLoginViaOTP, raw.allowLoginViaOtp)
    ),
    supportsRefreshToken: toBool(raw.supportsRefreshToken),
    enabled: raw.enabled !== false,
    accessTokenExpiryInSecs: raw.accessTokenExpiryInSecs,
    refreshTokenExpiryInSecs: raw.refreshTokenExpiryInSecs,
    creationDate: firstDefined(
      raw.createdAt,
      raw.creationDate,
      raw.createdDate,
      raw.createdOn
    ),
    lastModifiedDate: firstDefined(
      raw.updatedAt,
      raw.lastModifiedDate,
      raw.lastModified
    ),
  };
};

export const normalizeClientList = (payload) => {
  const source = unwrapClientPayload(payload);
  let list = [];
  if (Array.isArray(payload)) list = payload.map(normalizeClient);
  else if (Array.isArray(payload?.content)) {
    list = payload.content.map(normalizeClient);
  } else if (Array.isArray(payload?.data)) {
    list = payload.data.map(normalizeClient);
  } else if (Array.isArray(payload?.clients)) {
    list = payload.clients.map(normalizeClient);
  } else if (source && (source.clientId || source.id || source.name)) {
    list = [normalizeClient(source)];
  }

  return sortByRecent(list);
};

export const formatClientDate = (value) => {
  if (value === undefined || value === null || value === "") return "—";
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric > 0) {
    const millis = numeric < 1e12 ? numeric * 1000 : numeric;
    const parsed = dayjs(millis);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm") : "—";
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm") : "—";
};

export const pickClientSecret = (payload = {}) =>
  firstDefined(
    payload.clientSecret,
    payload.secret,
    payload.token,
    payload.password,
    payload?.data?.clientSecret,
    payload?.data?.secret,
    payload?.data?.token
  );

export const matchesClientSearch = (client, filters = {}) => {
  const name = String(filters.name || "").trim().toLowerCase();
  if (name && !String(client.name || "").toLowerCase().includes(name)) {
    return false;
  }
  return true;
};
